import { t } from "i18next"
import { capitalizeFirstLetter } from "utils"
import { customDateFormat, dateTimeFunction } from "utils/date"
import { getURLString, getUserMappingKey } from "utils/reports"

const getUserData = (item, parentId) => {
  let obj
  if (item?.data) {
    obj = {
      ...item.data,
      id: item.data.userId,
      name: item.data.userName,
      parentId: parentId,
      ...(item?.children && { children: item.children.map(e => getUserData(e, parentId)) }),
      joiningDate: customDateFormat(dateTimeFunction({ date: item?.data?.joiningDate, Default: true })),
      isLineManager: !!item?.children?.length,
      isTelesales: item?.data?.designation === "TSR" || item?.data?.designation === "TSRAFF",
    }
  } else {
    obj = {
      ...item,
      type: "region",
      id: item?.cities ? "region-" + item?.regionId : item?.cityId,
      name: item?.cityName || item.regionName,
      parentId: parentId,
      ...(item?.cities && {
        children: item.cities.map(e => getUserData(e, item?.cities ? "region-" + item?.regionId : item?.cityId)),
      }),
    }
  }
  return obj
}

const getNestedUserData = items => {
  return items?.length <= 1 ? (!!items?.[0]?.children?.length ? getNestedUserData(items?.[0]?.children) : items) : items
}

export const hierarchicalStaffTransformer = staffArray => {
  const transformedArray = staffArray.map(item => {
    const { data, children } = item
    return {
      title: data?.name,
      value: data?.id?.toString(),
      key: data?.id.toString(),
      regionId: data?.regionId,
      ...(children?.length && { children: hierarchicalStaffTransformer(children) }),
    }
  })
  return transformedArray
}

export const analyticsTransformer = response => {
  let listKeys = []
  Object.keys(response?.comparison?.staticKeys).forEach((item, index) => {
    listKeys.push({
      key: item,
      label: t(capitalizeFirstLetter(item)),
      title: t(capitalizeFirstLetter(item)),
      children: [],
    })
    Object.keys(response?.comparison?.staticKeys[item]).forEach(e => {
      listKeys[index].children.push({
        ...response.comparison.staticKeys[item][e],
        key: e,
        parent: item,
      })
    })
  })

  return {
    ...response,
    listKeys,
    defaultComparison: [
      { label: t("Qualified Calls"), short: "Q Calls", key: "qualifiedCalls", parent: "calls" },
      { label: t("Meetings Pushed"), short: "MET PUSH", key: "meetingPushed", parent: "meetings" },
    ],
  }
}

export const userKpiTransformer = (response, req, params) => {
  let listKeys = []

  const parentKeys = Object.keys(response?.userKpi?.staticKeys || {})
    .map(parentKey => ({
      parentKey,
      order: response.userKpi.staticKeys[parentKey]?.order ?? 0,
    }))
    .sort((a, b) => a.order - b.order)

  parentKeys.map(({ parentKey }) => {
    const children = Object.keys(response?.userKpi?.staticKeys[parentKey] || {})
      .filter(childKey => childKey !== "order") // ignore order key
      .map(childKey => {
        const { order, ...childData } = response.userKpi.staticKeys[parentKey][childKey] || {}
        return {
          type: childData?.type ? childData.type : childData?.hover ? "hover" : "number",
          ...childData,
          key: childKey,
          slopeKey: `${childKey}Slope`,
          parent: parentKey,
          hoverKeys: childData?.hover ? childData?.hoverKeys : {},
          urlMapping: {
            url: getURLString(response?.filters, params, childData, parentKey, childKey),
            userMappingKey: getUserMappingKey(childData),
          },
          order: order ?? 0,
        }
      })
      .sort((a, b) => a.order - b.order)
    listKeys.push({
      key: parentKey,
      label: t(capitalizeFirstLetter(parentKey)),
      title: t(capitalizeFirstLetter(parentKey)),
      children,
    })
  })

  const userKpi = {
    ...response.userKpi,
    data: response.userKpi.data.map(item => getUserData(item, item?.data?.userId || item?.regionId)),
  }

  return {
    ...response,
    listKeys,
    userKpi,
    staffRegionComparison: { data: getNestedUserData(response?.userKpi?.data.map(item => getUserData(item))) },
    defaultComparison: [
      { label: t("Qualified Calls"), short: "Q Calls", key: "qualifiedCalls", parent: "calls" },
      { label: t("Meetings Pushed"), short: "MET PUSH", key: "meetingPushed", parent: "meetings" },
    ],
  }
}

export const clientsTransformer = response => {
  const funnel = {
    list: [
      {
        label: t("Leads Allocated"),
        key: t("Leads Allocated"),
        count: response?.conversion?.leadsAllocated,
        percentage: response?.conversion?.leadsAllocatedPercentage,
        backgroundColor: "#8fa2c6",
      },
      {
        label: t("Leads Worked"),
        key: t("Leads Worked"),
        count: response?.conversion?.leadsWorked,
        percentage: response?.conversion?.leadsWorkedPercentage,
        backgroundColor: "#7c99ab",
      },
      {
        label: t("Qualified Calls"),
        key: t("QC"),
        count: response?.conversion?.calls,
        percentage: response?.conversion?.callsPercentage,
        backgroundColor: "#75c9a2",
        // redirectRoute: `/en${LEADS_LISTING_PATH}?status=22`,
      },
      {
        label: t("Meetings Pushed"),
        key: t("MP"),
        count: response?.conversion?.meetingsPushed,
        percentage: response?.conversion?.meetingsPushedPercentage,
        backgroundColor: "#509f8f",
        // redirectRoute: `/en${LEADS_LISTING_PATH}?status=15`,
      },
      // {
      //   label:t("Meetings Done"),
      //   key: "MD",
      //   count: response?.conversion?.meetingsDone || 0,
      //   percentage: response?.conversion?.meetingsDonePercentage?.toFixed() || 0 + "%",
      //   backgroundColor: "#1c6758",
      //   redirectRoute: `/en${LEADS_LISTING_PATH}?status=5`,
      // },
    ],
  }
  return {
    ...response,
    funnel,
  }
}

export const callsMeetingsTransformer = (response, req, params) => {
  const loadType = params?.load || null
  const callsObj = { ...response?.calls?.staticKeys?.bars }

  const callKeys = Object.keys(callsObj)
  const uniqueCallsData =
    (loadType == "calls" &&
      callKeys.map(e => {
        return {
          ...response?.calls?.[e],
          callType: callsObj?.[e],
          type: t("Unique"),
          value: response?.calls?.[e]?.unique || 0,
          total: response?.calls?.[e]?.total,
        }
      })) ||
    {}

  const duplicateCallsData =
    (loadType == "calls" &&
      callKeys?.map(e => {
        return {
          ...response?.calls?.[e],
          callType: callsObj?.[e],
          type: t("Repeat"),
          value: response?.calls?.[e]?.total - response?.calls?.[e]?.unique || 0,
          total: response?.calls?.[e]?.total,
        }
      })) ||
    {}

  const getMeetingsPushed =
    (loadType == "meetings_pushed" &&
      Object.keys(response?.meetings?.staticKeys?.bars)?.map(e => {
        return {
          ...response?.meetings?.[e],
          meetingType: response?.meetings?.staticKeys?.bars?.[e],
          type: t("Count"),
          value: response?.meetings?.[e] || 0,
        }
      })) ||
    {}
  const meetingData = loadType == "meetings_pushed" ? getMeetingsPushed : {}
  const callData = loadType == "calls" ? duplicateCallsData.concat(uniqueCallsData) : {} //concat the above two arrays for call-bar-chart
  return {
    ...response,
    calls: { ...response?.calls, total: response?.calls?.totalCalls, avgPerDay: response?.calls?.avgCallsDay },
    meetings_pushed: {
      ...response?.meetings,
      total: response?.meetings?.totalMeetingsPushed,
      avgPerDay: response?.meetings?.avgMeetingsPushedDay,
    },
    callData: callData,
    meetingData: meetingData,
  }
}
